import { AlertCircle, BookOpen, Building, Calendar, Check, Clock, CreditCard, FileText, GraduationCap, IndianRupee, Mail, Monitor, Phone, Upload, User, Users, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

// Import the QR code image (placeholder since we can't import actual images in this environment)
import qrCodeImage from '../assets/images/QR code gpay.jpeg';

interface FormData {
  name: string;
  email: string;
  phone: string;
  collegeName: string;
  degree: string;
  yearOfPassout: string;
  course: string;
  paymentScreenshot: File | null;
  paymentType: 'before_placement' | 'after_placement';
  paymentAmount: number;
  agreesToTerms: boolean;
}

interface FormErrors {
  [key: string]: string;
}

interface PaymentHistory {
  beforePlacement: number;
  afterPlacement: number;
  totalPaid: number;
  remainingBalance: number;
  isFullyPaid: boolean;
  hasCompletedSlotSelection: boolean;
  isFirstTimeUser: boolean;
}

interface SubmissionResult {
  status: 'success' | 'error';
  message?: string;
  success?: boolean;
  paymentHistory?: PaymentHistory;
}

interface SlotData {
  sessionType: 'morning' | 'evening' | 'weekend' | '';
  selectedSlot: string;
  mode: 'online' | 'offline' | '';
}

const StudentRegistrationForm: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<'registration' | 'slot-selection' | 'completion'>('registration');
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(true);
  

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    collegeName: '',
    degree: '',
    yearOfPassout: '',
    course: '',
    paymentScreenshot: null,
    paymentType: 'before_placement',
    paymentAmount: 0,
    agreesToTerms: false
  });

  const [slotData, setSlotData] = useState<SlotData>({
    sessionType: '',
    selectedSlot: '',
    mode: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory | null>(null);
  const [checkingHistory, setCheckingHistory] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);

  const courseOptions = [
    { value: '', label: 'Select a course' },
    { value: 'full-stack-java', label: 'Full Stack Java Development' },
    { value: 'Python full-stack development', label: 'Python full-stack development' },
    { value: 'Data Analytics', label: 'Data Analytics' },
    { value: 'business-analytics', label: 'Business Analytics' },
  ];

  const TOTAL_COURSE_FEE = 30000;
  const BEFORE_PLACEMENT_FEE = 15000;
  const AFTER_PLACEMENT_FEE = 15000;

  // Terms and Conditions content
  const termsAndConditions = [
    "The duration of the course is 3 month validity",
    "Students are required to complete all weekly assessments. Unexcused absences from these assessments will be subject to appropriate action.",
    "Following the initial payment, the remaining balance is due in full on the program's official start date. A 48-hour grace period is granted for any outstanding balance.",
    "All official updates and program information will be disseminated via email. Students are responsible for checking their email regularly. The management is not responsible for missed communications due to unchecked email accounts.",
    "Students are required to provide timely updates on the completion of their daily tasks, adhering to established deadlines.",
    "Students experiencing difficulty comprehending new concepts are required to notify the management within 48 hours of the concept's introduction. Failure to do so within this timeframe absolves the management of responsibility for addressing the learning challenges.",
    "Internship will be only scheduled depends upon your weekly assessment performance",
    "Consistent performance below expectations in assessments may necessitate an extended timeframe for program completion.",
    "Consistent communication with the management is mandatory throughout the duration of each project, beginning with the project's start. The management will not be held accountable for errors or necessary updates identified post-project completion if regular progress updates have not been provided",
    "Any instance of academic dishonesty or malpractice during assessments will be subject to disciplinary action, up to and including termination from the program.",
    "Attendance at all scheduled interviews is mandatory. Failure to attend will result in consequences as determined by the program.",
    "Students are required to sign the daily work done sheet only upon demonstrating comprehension of the daily topics covered.",
    "Students will be certified as having completed the course only upon successful completion and submission of all required daily tasks, weekly assessments and projects. Fulfilment of this requirement is mandatory for course completion.",
    "If the student secures a job through Vista Tech's placement support, the remaining ₹15,000 must be paid within 10 working days of receiving the offer letter. Failure to pay the remaining amount after placement may result in certificate withholding and potential legal action.",
    "Internship and Live Project enable to the candidates depends upon their academic performance",
    "In weekly assessment performance, students must obtain a minimum of 50 marks.",
    "Placement support includes resume building, mock interviews, referrals, and interview scheduling.",
    "Two consecutive days of leave are not permitted in this program. Any leave requests must be submitted with prior notice",
    "Once the timing of the batch is finalized, it will be never changed according to the candidates",
    "Prior to the commencement of class, students are requested to arrange themselves comfortably, regardless of the class format (online or offline)",
    "For the duration of online classes, students are expected to remain present. Leaving the online classroom before the scheduled end time will result in an absence being recorded.",
    "The initial ₹15,000 is non-refundable under any circumstances. No refund or fee adjustments will be entertained once the training begins.",
    "Upon fulfillment of the aforementioned terms and circumstances, the firm offers a 100% placement",
    "Violation of any of the aforementioned terms and conditions may result in the immediate cancellation of your refund, at the discretion of the company, based on the severity of the infraction and the explanation provided"
  ];

  const checkPaymentHistory = async () => {
    if (!formData.email) return;
    
    setCheckingHistory(true);
    try {
      const scriptUrl = 'https://script.google.com/macros/s/AKfycbxvs_SLkX3emouS7PU-fR6QaiOzCtRg5-9nHE2fOgtyr8XICj8jLjjWgupMPpjaA0NU/exec';
      
      const response = await fetch(`${scriptUrl}?action=checkPaymentHistory&email=${encodeURIComponent(formData.email)}`, {
        method: 'GET',
        mode: 'cors'
      });
      
      const result = await response.json();
      
      if (result.status === 'success' && result.paymentHistory) {
        const history = result.paymentHistory;
        
        const isFirstTime = history.totalPaid === 0;
        const hasCompletedSlotSelection = history.hasCompletedSlotSelection || false;
        
        setIsFirstTimeUser(isFirstTime);
        setPaymentHistory({
          ...history,
          hasCompletedSlotSelection,
          isFirstTimeUser: isFirstTime
        });
        
        // Set payment type and amount based on history
        if (history.beforePlacement >= BEFORE_PLACEMENT_FEE) {
          const afterPlacementRemaining = Math.max(0, AFTER_PLACEMENT_FEE - history.afterPlacement);
          setFormData(prev => ({
            ...prev,
            paymentType: 'after_placement',
            paymentAmount: afterPlacementRemaining,
            agreesToTerms: !isFirstTime
          }));
        } else {
          const beforePlacementRemaining = Math.max(0, BEFORE_PLACEMENT_FEE - history.beforePlacement);
          setFormData(prev => ({
            ...prev,
            paymentType: 'before_placement',
            paymentAmount: beforePlacementRemaining
          }));
        }
      } else {
        setIsFirstTimeUser(true);
        setPaymentHistory({
          beforePlacement: 0,
          afterPlacement: 0,
          totalPaid: 0,
          remainingBalance: TOTAL_COURSE_FEE,
          isFullyPaid: false,
          hasCompletedSlotSelection: false,
          isFirstTimeUser: true
        });
        setFormData(prev => ({
          ...prev,
          paymentAmount: BEFORE_PLACEMENT_FEE
        }));
      }
    } catch (error) {
      console.error('Error checking payment history:', error);
    } finally {
      setCheckingHistory(false);
    }
  };

  useEffect(() => {
    if (formData.email && formData.email.includes('@')) {
      checkPaymentHistory();
    }
  }, [formData.email]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.collegeName.trim()) {
      newErrors.collegeName = 'College name is required';
    }

    if (!formData.degree.trim()) {
      newErrors.degree = 'Degree is required';
    }

    if (!formData.yearOfPassout.trim()) {
      newErrors.yearOfPassout = 'Year of passout is required';
    } else {
      const year = parseInt(formData.yearOfPassout);
      const currentYear = new Date().getFullYear();
      if (year < 1950 || year > currentYear + 4) {
        newErrors.yearOfPassout = 'Please enter a valid year';
      }
    }

    if (!formData.course) {
      newErrors.course = 'Please select a course';
    }

    if (isFirstTimeUser && !formData.agreesToTerms) {
      newErrors.agreesToTerms = 'You must agree to the terms and conditions';
    }

    if (paymentCompleted && !formData.paymentScreenshot && formData.paymentAmount > 0) {
      newErrors.paymentScreenshot = 'Payment screenshot is required';
    }

    if (formData.paymentAmount <= 0 && !paymentHistory?.isFullyPaid) {
      newErrors.paymentAmount = 'Payment amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePaymentTypeChange = (type: 'before_placement' | 'after_placement') => {
    // Check if the selected payment type is already fully paid
    if (type === 'before_placement' && paymentHistory && paymentHistory.beforePlacement >= BEFORE_PLACEMENT_FEE) {
      alert('Before Placement payment is already completed. Please select After Placement.');
      return;
    }
    
    if (type === 'after_placement' && paymentHistory && paymentHistory.afterPlacement >= AFTER_PLACEMENT_FEE) {
      alert('After Placement payment is already completed.');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      paymentType: type,
      paymentAmount: type === 'before_placement' 
        ? Math.max(0, BEFORE_PLACEMENT_FEE - (paymentHistory?.beforePlacement || 0))
        : Math.max(0, AFTER_PLACEMENT_FEE - (paymentHistory?.afterPlacement || 0))
    }));
  };

  const handlePaymentAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const amount = parseFloat(value) || 0;
    
    const maxAmount = formData.paymentType === 'before_placement' 
      ? Math.max(0, BEFORE_PLACEMENT_FEE - (paymentHistory?.beforePlacement || 0))
      : Math.max(0, AFTER_PLACEMENT_FEE - (paymentHistory?.afterPlacement || 0));
    
    // Validate amount
    if (amount > maxAmount) {
      setErrors(prev => ({
        ...prev,
        paymentAmount: `Amount cannot exceed ₹${maxAmount}`
      }));
    } else if (amount <= 0) {
      setErrors(prev => ({
        ...prev,
        paymentAmount: 'Amount must be greater than 0'
      }));
    } else {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.paymentAmount;
        return newErrors;
      });
    }
    
    setFormData(prev => ({
      ...prev,
      paymentAmount: amount
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({
      ...prev,
      paymentScreenshot: file
    }));
    
    if (errors.paymentScreenshot) {
      setErrors(prev => ({
        ...prev,
        paymentScreenshot: ''
      }));
    }
  };

  const handlePaymentClick = () => {
    if (validateForm()) {
      if (paymentHistory?.isFullyPaid) {
        alert('You have already completed all payments for this course!');
        return;
      }
      setShowPayment(true);
    }
  };

  const copyUpiId = () => {
    navigator.clipboard.writeText('9894449002@okbizaxis');
    alert('UPI ID copied to clipboard!');
  };

  const handlePaymentConfirmClick = () => {
    // Validate payment amount
    const maxAmount = formData.paymentType === 'before_placement' 
      ? Math.max(0, BEFORE_PLACEMENT_FEE - (paymentHistory?.beforePlacement || 0))
      : Math.max(0, AFTER_PLACEMENT_FEE - (paymentHistory?.afterPlacement || 0));
    
    if (formData.paymentAmount <= 0) {
      alert('Please enter a valid payment amount greater than 0');
      return;
    }
    
    if (formData.paymentAmount > maxAmount) {
      alert(`Payment amount cannot exceed ₹${maxAmount}`);
      return;
    }
    
    if (!formData.paymentScreenshot) {
      alert('Please upload payment screenshot');
      return;
    }
    
    // Show confirmation modal
    setShowPaymentConfirmation(true);
  };

  const confirmPayment = () => {
    setPaymentCompleted(true);
    setShowPaymentConfirmation(false);
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const submitToGoogleSheets = async (data: FormData): Promise<SubmissionResult> => {
    const scriptUrl = 'https://script.google.com/macros/s/AKfycbxvs_SLkX3emouS7PU-fR6QaiOzCtRg5-9nHE2fOgtyr8XICj8jLjjWgupMPpjaA0NU/exec';
    
    console.log('=== ENHANCED FORM SUBMISSION DEBUG ===');
    console.log('Form data being sent:', {
      name: data.name,
      email: data.email,
      phone: data.phone,
      collegeName: data.collegeName,
      degree: data.degree,
      yearOfPassout: data.yearOfPassout,
      course: data.course,
      paymentType: data.paymentType,
      paymentAmount: data.paymentAmount,
      agreesToTerms: data.agreesToTerms,
      hasScreenshot: !!data.paymentScreenshot,
      screenshotName: data.paymentScreenshot?.name,
      screenshotSize: data.paymentScreenshot?.size,
      isFirstTimeUser: isFirstTimeUser
    });

    try {
      console.log('Submitting to Google Sheets...');
      
      let response;
      let result;
      
      if (data.paymentScreenshot && data.paymentAmount > 0) {
        console.log('Converting screenshot to base64...');
        
        try {
          const base64Data = await fileToBase64(data.paymentScreenshot);
          console.log('File converted to base64, length:', base64Data.length);
          
          const urlEncodedData = new URLSearchParams();
          urlEncodedData.append('action', 'submitPayment');
          urlEncodedData.append('name', data.name);
          urlEncodedData.append('email', data.email);
          urlEncodedData.append('phone', data.phone);
          urlEncodedData.append('collegeName', data.collegeName);
          urlEncodedData.append('degree', data.degree);
          urlEncodedData.append('yearOfPassout', data.yearOfPassout);
          urlEncodedData.append('course', data.course);
          urlEncodedData.append('paymentType', data.paymentType);
          urlEncodedData.append('paymentAmount', data.paymentAmount.toString());
          urlEncodedData.append('agreesToTerms', data.agreesToTerms.toString());
          urlEncodedData.append('timestamp', new Date().toISOString());
          urlEncodedData.append('paymentScreenshotBase64', base64Data);
          urlEncodedData.append('screenshotFilename', data.paymentScreenshot.name);
          urlEncodedData.append('screenshotMimeType', data.paymentScreenshot.type);
          urlEncodedData.append('isFirstTimeUser', isFirstTimeUser.toString());
          
          response = await fetch(scriptUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: urlEncodedData.toString(),
            mode: 'cors'
          });
          
          result = await response.text();
          console.log('Payment submission response:', result);
          
        } catch (base64Error) {
          console.error('Base64 conversion error:', base64Error);
          throw new Error('Failed to process screenshot: ' + base64Error);
        }
      } else {
        const urlEncodedData = new URLSearchParams();
        urlEncodedData.append('action', 'submitRegistration');
        urlEncodedData.append('name', data.name);
        urlEncodedData.append('email', data.email);
        urlEncodedData.append('phone', data.phone);
        urlEncodedData.append('collegeName', data.collegeName);
        urlEncodedData.append('degree', data.degree);
        urlEncodedData.append('yearOfPassout', data.yearOfPassout);
        urlEncodedData.append('course', data.course);
        urlEncodedData.append('agreesToTerms', data.agreesToTerms.toString());
        urlEncodedData.append('timestamp', new Date().toISOString());
        urlEncodedData.append('isFirstTimeUser', isFirstTimeUser.toString());
        
        response = await fetch(scriptUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: urlEncodedData.toString(),
          mode: 'cors'
        });
        
        result = await response.text();
        console.log('Registration response:', result);
      }
      
      console.log('Response status:', response.status);
      console.log('Response status text:', response.statusText);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      try {
        const jsonResult = JSON.parse(result);
        console.log('Parsed JSON result:', jsonResult);
        return {
          status: jsonResult.status || 'success',
          message: jsonResult.message || 'Data submitted successfully',
          success: jsonResult.status === 'success',
          paymentHistory: jsonResult.paymentHistory
        };
      } catch (parseError) {
        console.log('Response is not JSON, treating as success');
        return { 
          status: 'success', 
          success: true,
          message: 'Form submitted successfully'
        };
      }
    } catch (error) {
      console.error('=== SUBMISSION ERROR ===');
      console.error('Error submitting form:', error);
      
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        return {
          status: 'error',
          message: 'Network error: Please check your internet connection and try again.',
          success: false
        };
      }
      
      return { 
        status: 'error', 
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        success: false 
      };
    }
  };

  const submitSlotRequest = async () => {
    if (!slotData.sessionType || !slotData.mode) {
      alert('Please select session type, time slot, and mode');
      return;
    }

    setIsSubmitting(true);

    try {
      const scriptUrl = 'https://script.google.com/macros/s/AKfycbxvs_SLkX3emouS7PU-fR6QaiOzCtRg5-9nHE2fOgtyr8XICj8jLjjWgupMPpjaA0NU/exec';
      
      const urlEncodedData = new URLSearchParams();
      urlEncodedData.append('action', 'submitSlotRequest');
      urlEncodedData.append('name', formData.name);
      urlEncodedData.append('email', formData.email);
      urlEncodedData.append('phone', formData.phone);
      urlEncodedData.append('course', formData.course);
      urlEncodedData.append('sessionType', slotData.sessionType);
      urlEncodedData.append('mode', slotData.mode);
      urlEncodedData.append('status', 'pending');
      urlEncodedData.append('timestamp', new Date().toISOString());
      
      const response = await fetch(scriptUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
        mode: 'cors'
      });
      
      const result = await response.text();
      
      if (response.ok) {
        setCurrentStep('completion');
        alert('Session preference submitted successfully! You will receive an email once your session is confirmed.');
      } else {
        throw new Error('Failed to submit session preference');
      }
    } catch (error) {
      console.error('Error submitting session preference:', error);
      alert('Error submitting session preference. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (formData.paymentAmount > 0 && !paymentCompleted) {
      alert('Please complete the payment process first');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitToGoogleSheets(formData);

      if (result.status === 'success' || result.success) {
        if (result.paymentHistory) {
          setPaymentHistory(result.paymentHistory);
        }

        // Mirror to MongoDB so the lead appears in the Unassigned Leads tracker tab
        fetch('https://vistatech-serversss-production.up.railway.app/api/enroll', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name:       formData.name,
            email:      formData.email,
            phone:      formData.phone,
            course:     formData.course,
            regPaid:    formData.paymentAmount > 0 ? formData.paymentAmount : 1000,
            sessionType: 'group',
          }),
        }).catch(() => { /* non-critical — Google Sheets is source of truth for form */ });

        setRegistrationCompleted(true);
        
        if (isFirstTimeUser) {
          setCurrentStep('slot-selection');
          alert('Registration completed successfully! Please select your preferred session type.');
        } else {
          setCurrentStep('completion');
          alert('Balance payment completed successfully! Your registration is now complete.');
        }
      } else {
        alert('Error submitting registration: ' + (result.message || 'Please try again.'));
      }
    } catch (error) {
      console.error('Submission error:', error);
      alert('Error submitting registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Payment Confirmation Modal
  const PaymentConfirmationModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-center mb-4">
          <AlertCircle className="w-12 h-12 text-yellow-500" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">Confirm Payment Amount</h2>
        
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-700 mb-3">Please verify your payment details carefully:</p>
          
          <div className="space-y-2 mb-3">
            <div className="flex justify-between">
              <span className="font-medium">Payment Type:</span>
              <span className="capitalize">{formData.paymentType.replace('_', ' ')}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-medium">Amount Paid:</span>
              <span className="text-xl font-bold text-green-600">₹{formData.paymentAmount}</span>
            </div>
          </div>
          
          <div className="bg-white border border-yellow-300 rounded p-3">
            <p className="text-xs text-gray-600 mb-2">
              <strong>Important:</strong> Make sure the amount you entered (₹{formData.paymentAmount}) matches EXACTLY with the amount you paid via UPI/QR code.
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setShowPaymentConfirmation(false)}
            className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-400 transition-colors"
          >
            Cancel & Recheck
          </button>
          <button
            onClick={confirmPayment}
            className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            Confirm Payment
          </button>
        </div>
      </div>
    </div>
  );

  // Terms Modal Component
  const TermsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Vista Tech - Terms and Conditions
          </h2>
          <button
            onClick={() => setShowTermsModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-4">
            {termsAndConditions.map((term, index) => (
              <div key={index} className="flex items-start">
                <span className="inline-block w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full text-sm font-medium flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  {index + 1}
                </span>
                <p className="text-sm text-gray-700 leading-relaxed">{term}</p>
              </div>
            ))}
          </div>
          
          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-900 mb-2">VISTA TECH</p>
              <p className="text-xs text-gray-600">
                For complete terms, visit: 
                <a href="https://vistatech.org/Terms-and-Conditions" 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   className="text-indigo-600 underline ml-1">
                  https://vistatech.org/Terms-and-Conditions
                </a>
              </p>
            </div>
          </div>
        </div>
        
        <div className="p-6 border-t bg-gray-50">
          <button
            onClick={() => setShowTermsModal(false)}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  // Session Selection Component
  const SessionSelectionPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-indigo-600 mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">Select Your Session Preferences</h1>
            </div>
            <p className="text-gray-600">Choose your preferred session timing and mode for <span className="font-semibold text-indigo-600">{formData.course}</span></p>
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-green-800 font-medium">Registration Completed ✓</p>
              <p className="text-sm text-green-600 mt-1">Now select your preferred session to complete the process</p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Session Type Selection */}
            <div>
              <label className="text-lg font-medium text-gray-700 mb-4 block flex items-center">
                <Calendar className="w-5 h-5 mr-2" />
                Choose Session Type
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {['morning', 'evening', 'weekend'].map((session) => (
                  <button
                    key={session}
                    onClick={() => setSlotData(prev => ({ ...prev, sessionType: session as any, selectedSlot: '' }))}
                    className={`p-6 rounded-lg border-2 transition-all text-center hover:shadow-lg ${
                      slotData.sessionType === session
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-md'
                        : 'border-gray-300 hover:border-indigo-300'
                    }`}
                  >
                    <div className="text-lg font-semibold capitalize mb-2">{session}</div>
                    <div className="text-sm text-gray-600">
                      {session === 'morning' && 'Weekdays: 9 AM - 12 PM'}
                      {session === 'evening' && 'Weekdays: 5 PM - 8 PM'}
                      {session === 'weekend' && 'Saturday & Sunday'}
                    </div>
                    {slotData.sessionType === session && (
                      <div className="mt-2">
                        <Check className="w-5 h-5 text-indigo-600 mx-auto" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Mode Selection */}
            {slotData.sessionType && (
              <div>
                <label className="text-lg font-medium text-gray-700 mb-4 block flex items-center">
                  <Monitor className="w-5 h-5 mr-2" />
                  Choose Learning Mode
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    onClick={() => setSlotData(prev => ({ ...prev, mode: 'online' }))}
                    className={`p-6 rounded-lg border-2 transition-all text-center hover:shadow-lg ${
                      slotData.mode === 'online'
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-md'
                        : 'border-gray-300 hover:border-indigo-300'
                    }`}
                  >
                    <Monitor className="w-8 h-8 mx-auto mb-3 text-blue-600" />
                    <div className="text-lg font-semibold mb-2">Online Classes</div>
                    <div className="text-sm text-gray-600">
                      Join from anywhere with stable internet connection
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      • Zoom/Google Meet sessions<br />
                      • Interactive virtual learning<br />
                      • Record sessions for review
                    </div>
                    {slotData.mode === 'online' && (
                      <div className="mt-3">
                        <Check className="w-5 h-5 text-indigo-600 mx-auto" />
                      </div>
                    )}
                  </button>
                  
                  <button
                    onClick={() => setSlotData(prev => ({ ...prev, mode: 'offline' }))}
                    className={`p-6 rounded-lg border-2 transition-all text-center hover:shadow-lg ${
                      slotData.mode === 'offline'
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700 shadow-md'
                        : 'border-gray-300 hover:border-indigo-300'
                    }`}
                  >
                    <Users className="w-8 h-8 mx-auto mb-3 text-green-600" />
                    <div className="text-lg font-semibold mb-2">In-Person Classes</div>
                    <div className="text-sm text-gray-600">
                      Attend classes at our training center
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      • Face-to-face interaction<br />
                      • Hands-on learning experience<br />
                      • Direct mentor guidance
                    </div>
                    {slotData.mode === 'offline' && (
                      <div className="mt-3">
                        <Check className="w-5 h-5 text-indigo-600 mx-auto" />
                      </div>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Selected Session Summary */}
            {slotData.sessionType && slotData.mode && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Check className="w-5 h-5 text-green-600 mr-2" />
                  Selected Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Student Name:</p>
                    <p className="font-medium text-gray-900">{formData.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Course:</p>
                    <p className="font-medium text-gray-900">{formData.course}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone:</p>
                    <p className="font-medium text-gray-900">{formData.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Session Type:</p>
                    <p className="font-medium text-gray-900 capitalize">{slotData.sessionType}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-600">Learning Mode:</p>
                    <p className="font-medium text-gray-900 capitalize flex items-center">
                      {slotData.mode === 'online' ? (
                        <>
                          <Monitor className="w-4 h-4 mr-1" />
                          Online
                        </>
                      ) : (
                        <>
                          <Users className="w-4 h-4 mr-1" />
                          In-Person
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                onClick={submitSlotRequest}
                disabled={!slotData.sessionType || !slotData.mode || isSubmitting}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center ${
                  !slotData.sessionType || !slotData.mode || isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Submit Session Preference
                  </>
                )}
              </button>
            </div>

            {/* Note */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> Your session preference will be reviewed by our admin team. You will receive an email notification once your session is confirmed or if any changes are needed.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Completion Page
  const CompletionPage = () => (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isFirstTimeUser ? 'Request Submitted Successfully!' : 'Payment Completed Successfully!'}
            </h1>
            <p className="text-gray-600">
              {isFirstTimeUser 
                ? 'Your session preference is waiting for admin approval'
                : 'Your balance payment has been processed successfully'
              }
            </p>
          </div>

          {isFirstTimeUser ? (
            <>
              <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center justify-center">
                  <Clock className="w-5 h-5 mr-2" />
                  What happens next?
                </h3>
                <div className="text-left space-y-3 text-gray-700">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <p>Admin will review your session preference within 24-48 hours</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <p>You will receive an email notification about approval/decline</p>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <p>If approved, you'll get class joining details</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h4 className="font-semibold text-gray-800 mb-4">Your Submitted Information:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                  <div className="text-left">
                    <p><strong>Name:</strong> {formData.name}</p>
                    <p><strong>Email:</strong> {formData.email}</p>
                    <p><strong>Phone:</strong> {formData.phone}</p>
                  </div>
                  <div className="text-left">
                    <p><strong>Course:</strong> {formData.course}</p>
                    <p><strong>Session:</strong> {slotData.sessionType?.charAt(0).toUpperCase() + slotData.sessionType?.slice(1)}</p>
                    <p><strong>Mode:</strong> {slotData.mode?.charAt(0).toUpperCase() + slotData.mode?.slice(1)}</p>
                    <p><strong>Status:</strong> <span className="text-yellow-600 font-medium">Pending Approval</span></p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-6">
                <p className="text-yellow-800 text-sm">
                  <strong>Important:</strong> Please check your email regularly for updates. Make sure to check your spam folder as well.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-600 mr-2" />
                  Balance Payment Complete
                </h3>
                <div className="text-gray-700">
                  <p className="mb-2">Your balance payment of ₹{formData.paymentAmount} has been successfully processed.</p>
                  <p>You can now continue with your ongoing course. Check your email for any updates from your instructor.</p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h4 className="font-semibold text-gray-800 mb-4">Payment Summary:</h4>
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between mb-2">
                    <span>Payment Amount:</span>
                    <span className="font-medium">₹{formData.paymentAmount}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span>Payment Type:</span>
                    <span className="font-medium capitalize">{formData.paymentType.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Course:</span>
                    <span className="font-medium">{formData.course}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
                <p className="text-blue-800 text-sm">
                  <strong>Next Steps:</strong> Continue attending your scheduled classes. Your instructor will contact you with any updates or additional requirements.
                </p>
              </div>
            </>
          )}

          <div className="text-center text-gray-500 text-sm">
            <p>Thank you for choosing Vista Tech's 50% Pay After Placement program!</p>
            <p className="mt-2">For any queries, contact us at support@vistatech.com</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Render based on current step
  if (currentStep === 'slot-selection') {
    return <SessionSelectionPage />;
  }

  if (currentStep === 'completion') {
    return <CompletionPage />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">50% Pay After Placement</h1>
            <p className="text-gray-600">Join our professional development programs</p>
            <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
              <p className="text-sm text-indigo-800">
                <strong>Course Fee Structure:</strong> Total ₹30,000 | Before Placement: ₹15,000 | After Placement: ₹15,000
              </p>
            </div>
          </div>

          {paymentHistory && (
            <div className={`mb-6 p-4 rounded-lg border ${
              isFirstTimeUser 
                ? 'bg-blue-50 border-blue-200' 
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <div className="flex items-center justify-center">
                <div className={`w-3 h-3 rounded-full mr-2 ${
                  isFirstTimeUser ? 'bg-blue-500' : 'bg-yellow-500'
                }`}></div>
                <p className={`text-sm font-medium ${
                  isFirstTimeUser ? 'text-blue-800' : 'text-yellow-800'
                }`}>
                  {isFirstTimeUser 
                    ? 'New Registration - First Time Payment' 
                    : 'Balance Payment - Existing Student'
                  }
                </p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Name Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 mr-2" />
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your full name"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Email Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 mr-2" />
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your email address"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              {checkingHistory && (
                <p className="text-blue-600 text-sm mt-1">Checking payment history...</p>
              )}
            </div>

            {/* Phone Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 mr-2" />
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your 10-digit phone number"
              />
              {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
            </div>

            {/* Payment History Display */}
            {paymentHistory && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment History</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Before Placement Paid:</p>
                    <p className="font-semibold text-green-600">₹{paymentHistory.beforePlacement}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">After Placement Paid:</p>
                    <p className="font-semibold text-green-600">₹{paymentHistory.afterPlacement}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Total Paid:</p>
                    <p className="font-semibold text-green-600">₹{paymentHistory.totalPaid}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Remaining Balance:</p>
                    <p className={`font-semibold ${paymentHistory.remainingBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      ₹{paymentHistory.remainingBalance}
                    </p>
                  </div>
                </div>
                {paymentHistory.isFullyPaid && (
                  <div className="mt-3 p-3 bg-green-100 rounded-lg">
                    <p className="text-green-800 font-semibold">✓ All payments completed!</p>
                  </div>
                )}
              </div>
            )}

            {/* College Name Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Building className="w-4 h-4 mr-2" />
                College Name
              </label>
              <input
                type="text"
                name="collegeName"
                value={formData.collegeName}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                  errors.collegeName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your college name"
              />
              {errors.collegeName && <p className="text-red-500 text-sm mt-1">{errors.collegeName}</p>}
            </div>

            {/* Degree Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <BookOpen className="w-4 h-4 mr-2" />
                Degree
              </label>
              <input
                type="text"
                name="degree"
                value={formData.degree}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                  errors.degree ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., B.Tech Computer Science, BCA, MCA"
              />
              {errors.degree && <p className="text-red-500 text-sm mt-1">{errors.degree}</p>}
            </div>

            {/* Year of Passout Field */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 mr-2" />
                Year of Passout
              </label>
              <input
                type="number"
                name="yearOfPassout"
                value={formData.yearOfPassout}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                  errors.yearOfPassout ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., 2024"
                min="1950"
                max="2030"
              />
              {errors.yearOfPassout && <p className="text-red-500 text-sm mt-1">{errors.yearOfPassout}</p>}
            </div>

            {/* Course Selection */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <GraduationCap className="w-4 h-4 mr-2" />
                Select Course
              </label>
              <select
                name="course"
                value={formData.course}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                  errors.course ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {courseOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.course && <p className="text-red-500 text-sm mt-1">{errors.course}</p>}
            </div>

            {/* Terms and Conditions - Only for first-time users */}
            {isFirstTimeUser && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="agreesToTerms"
                    name="agreesToTerms"
                    checked={formData.agreesToTerms}
                    onChange={handleInputChange}
                    className={`mr-3 mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded ${
                      errors.agreesToTerms ? 'border-red-500' : ''
                    }`}
                  />
                  <div className="flex-1">
                    <label htmlFor="agreesToTerms" className="text-sm text-gray-700 cursor-pointer">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-indigo-600 underline hover:text-indigo-800 font-medium"
                      >
                        Terms and Conditions
                      </button>
                      {' '}of Vista Tech's 50% Pay After Placement program. You can also view them at{' '}
                      <a 
                        href="https://vistatech.org/Terms-and-Conditions" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-indigo-600 underline hover:text-indigo-800 font-medium"
                      >
                        vistatech.org/Terms-and-Conditions
                      </a>
                    </label>
                    {formData.agreesToTerms && (
                      <div className="flex items-center mt-2 text-green-600">
                        <Check className="w-4 h-4 mr-1" />
                        <span className="text-sm">Terms accepted</span>
                      </div>
                    )}
                  </div>
                </div>
                {errors.agreesToTerms && <p className="text-red-500 text-sm mt-2">{errors.agreesToTerms}</p>}
              </div>
            )}

            {/* Notice for returning users */}
            {!isFirstTimeUser && (
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center">
                  <Check className="w-5 h-5 text-yellow-600 mr-2" />
                  <p className="text-yellow-800 text-sm">
                    <strong>Returning Student:</strong> You have already accepted our terms and conditions. This is a balance payment for your ongoing course.
                  </p>
                </div>
              </div>
            )}

            {/* Payment Status Summary - NEW */}
            {paymentHistory && !paymentHistory.isFullyPaid && (
              <div className="bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg mb-4">
                <h4 className="font-semibold text-gray-800 mb-3">Payment Status:</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className={`p-3 rounded ${paymentHistory.beforePlacement >= BEFORE_PLACEMENT_FEE ? 'bg-green-100 border border-green-300' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <p className="font-medium">Before Placement:</p>
                    <p className="text-lg font-bold">₹{paymentHistory.beforePlacement} / ₹{BEFORE_PLACEMENT_FEE}</p>
                    {paymentHistory.beforePlacement >= BEFORE_PLACEMENT_FEE && (
                      <p className="text-green-600 text-xs mt-1">✓ Completed</p>
                    )}
                  </div>
                  <div className={`p-3 rounded ${paymentHistory.afterPlacement >= AFTER_PLACEMENT_FEE ? 'bg-green-100 border border-green-300' : 'bg-yellow-50 border border-yellow-200'}`}>
                    <p className="font-medium">After Placement:</p>
                    <p className="text-lg font-bold">₹{paymentHistory.afterPlacement} / ₹{AFTER_PLACEMENT_FEE}</p>
                    {paymentHistory.afterPlacement >= AFTER_PLACEMENT_FEE && (
                      <p className="text-green-600 text-xs mt-1">✓ Completed</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Payment Section */}
            {!paymentHistory?.isFullyPaid && !showPayment ? (
              <button
                type="button"
                onClick={handlePaymentClick}
                className="w-full bg-indigo-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                {isFirstTimeUser ? 'Proceed to Payment' : 'Pay Balance Amount'}
              </button>
            ) : paymentHistory?.isFullyPaid ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                } text-white`}
              >
                {isSubmitting ? 'Submitting...' : 'Complete Registration (Already Paid)'}
              </button>
            ) : showPayment ? (
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Details</h3>
                
                {/* Payment Type Selection - UPDATED */}
                <div className="mb-4">
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Payment Type</label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => handlePaymentTypeChange('before_placement')}
                      disabled={Boolean(paymentHistory && paymentHistory.beforePlacement >= BEFORE_PLACEMENT_FEE)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.paymentType === 'before_placement'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-300 hover:border-gray-400'
                      } ${
                        paymentHistory && paymentHistory.beforePlacement >= BEFORE_PLACEMENT_FEE
                          ? 'opacity-50 cursor-not-allowed bg-gray-100'
                          : ''
                      }`}
                    >
                      <div className="text-sm font-medium">Before Placement</div>
                      <div className="text-xs text-gray-600">
                        ₹15,000
                        {paymentHistory && paymentHistory.beforePlacement >= BEFORE_PLACEMENT_FEE && (
                          <span className="block text-green-600 font-semibold">✓ Fully Paid</span>
                        )}
                      </div>
                    </button>
                    
                    <button
  type="button"
  onClick={() => handlePaymentTypeChange('after_placement')}
  disabled={paymentHistory ? paymentHistory.afterPlacement >= AFTER_PLACEMENT_FEE : false}
  className={`p-3 rounded-lg border-2 transition-all ${
    formData.paymentType === 'after_placement'
      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
      : 'border-gray-300 hover:border-gray-400'
  } ${
    paymentHistory && paymentHistory.afterPlacement >= AFTER_PLACEMENT_FEE
      ? 'opacity-50 cursor-not-allowed bg-gray-100'
      : ''
  }`}
>
                      <div className="text-sm font-medium">After Placement</div>
                      <div className="text-xs text-gray-600">
                        ₹15,000
                        {paymentHistory && paymentHistory.afterPlacement >= AFTER_PLACEMENT_FEE && (
                          <span className="block text-green-600 font-semibold">✓ Fully Paid</span>
                        )}
                      </div>
                    </button>
                  </div>
                </div>

                {/* Payment Amount Input - UPDATED */}
                <div className="mb-4">
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <IndianRupee className="w-4 h-4 mr-2" />
                    Payment Amount
                  </label>
                  <input
                    type="number"
                    name="paymentAmount"
                    value={formData.paymentAmount}
                    onChange={handlePaymentAmountChange}
                    min="1"
                    max={formData.paymentType === 'before_placement' 
                      ? Math.max(0, BEFORE_PLACEMENT_FEE - (paymentHistory?.beforePlacement || 0))
                      : Math.max(0, AFTER_PLACEMENT_FEE - (paymentHistory?.afterPlacement || 0))}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-lg font-semibold ${
                      errors.paymentAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter amount to pay"
                  />
                  {errors.paymentAmount && (
                    <p className="text-red-500 text-sm mt-1">{errors.paymentAmount}</p>
                  )}
                  {/* UPDATED Warning Message */}
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                    {(() => {
                      const maxAmount = formData.paymentType === 'before_placement' 
                        ? Math.max(0, BEFORE_PLACEMENT_FEE - (paymentHistory?.beforePlacement || 0))
                        : Math.max(0, AFTER_PLACEMENT_FEE - (paymentHistory?.afterPlacement || 0));
                      
                      return (
                        <>
                          <p className="text-xs text-yellow-800">
                            <strong>⚠️ Important:</strong>{' '}
                            {maxAmount > 0 ? (
                              <>
                                Maximum amount for {formData.paymentType.replace('_', ' ')}: <strong>₹{maxAmount}</strong>
                              </>
                            ) : (
                              <>
                                You have already paid the full amount for {formData.paymentType.replace('_', ' ')}. 
                                {formData.paymentType === 'before_placement' && 
                                 paymentHistory && 
                                 paymentHistory.afterPlacement < AFTER_PLACEMENT_FEE && (
                                  <span className="block mt-1 font-semibold text-green-700">
                                    👉 Please select "After Placement" to pay the remaining balance.
                                  </span>
                                )}
                              </>
                            )}
                          </p>
                          {maxAmount > 0 && (
                            <p className="text-xs text-yellow-800 mt-1">
                              Please enter the exact amount you paid via UPI/QR code.
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg border mb-4">
                  <p className="text-sm text-gray-600 mb-3 text-center">Pay ₹{formData.paymentAmount} using QR Code or UPI ID:</p>
                  
                  {/* QR Code Image */}
                  <div className="flex justify-center mb-4">
                    <div className="bg-white p-3 rounded-lg border-2 border-gray-200 shadow-sm">
                      <img
                        src={qrCodeImage}
                        alt="Payment QR Code"
                        className="w-48 h-48 object-contain"
                        onError={(e) => {
                          console.error('QR Code image failed to load');
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Separator */}
                  <div className="flex items-center mb-3">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="px-3 text-sm text-gray-500 bg-white">OR</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                  
                  {/* UPI ID Section */}
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Use UPI ID:</p>
                    <div className="flex items-center justify-between bg-gray-100 p-3 rounded">
                      <span className="font-mono text-lg">9894449002@okbizaxis</span>
                      <button
                        type="button"
                        onClick={copyUpiId}
                        className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>

                {formData.paymentAmount > 0 && (
                  <div className="mb-4">
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Payment Screenshot
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                        errors.paymentScreenshot ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.paymentScreenshot && <p className="text-red-500 text-sm mt-1">{errors.paymentScreenshot}</p>}
                    {formData.paymentScreenshot && (
                      <p className="text-green-600 text-sm mt-1">✓ Screenshot uploaded: {formData.paymentScreenshot.name}</p>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handlePaymentConfirmClick}
                  className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                    paymentCompleted
                      ? 'bg-green-100 text-green-800 cursor-default'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                  disabled={paymentCompleted}
                >
                  {paymentCompleted ? '✓ Payment Confirmed' : 'Confirm Payment'}
                </button>
              </div>
            ) : null}

            {/* Submit Button */}
            {paymentCompleted && !paymentHistory?.isFullyPaid && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                } text-white`}
              >
                {isSubmitting ? 'Submitting...' : 'Complete Registration'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Payment Confirmation Modal */}
      {showPaymentConfirmation && <PaymentConfirmationModal />}

      {/* Terms and Conditions Modal */}
      {showTermsModal && <TermsModal />}
    </div>
  );
};

export default StudentRegistrationForm;